"use client";

import {
  DatePickerV2,
  LiquidPillGroup,
  ModalButtons,
  SwitcherV2,
} from "components/ui/v2";
import { TagPickerV2 } from "components/ui/v2/composites/tag-picker-v2";
import { useMutation, useQuery } from "@apollo/client";
import { WIDTH_MD } from "@constants/global";
import { useWindowWidth } from "@react-hook/window-size";
import { Mail, Users } from "lucide-react";
import { resolveFormClose } from "components/ui/v2/form-v2-props";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { gqlCertificate, gqlCertificateUser, gqlUser } from "gql";
import {
  ICreateCertificateSendByTeamsInput,
  ISendCertificateInput,
} from "gql/Certificate-user/mutations";
import { ICertificate } from "gql/Certificates/queries";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { IArea } from "types/Area.type";
import { IUser } from "types/User.types";

export const ASSIGN_CERTIFICATE_EMAIL_FORM_ID = "assign-certificate-email-form";
export const ASSIGN_CERTIFICATE_TEAM_FORM_ID = "assign-certificate-team-form";

interface IAssignCertificateProps {
  certificate?: ICertificate | undefined;
  onClose?: () => void;
  hideHeader?: boolean;
  hideFooter?: boolean;
  activeTab?: "email" | "team";
  onTabChange?: (tab: "email" | "team") => void;
  onLoadingChange?: (loading: boolean) => void;
  emailFormId?: string;
  teamFormId?: string;
}

export const AssignCertificate = (props: IAssignCertificateProps) => {
  const {
    certificate,
    onClose: onCloseProp,
    hideHeader = false,
    hideFooter = false,
    activeTab: controlledTab,
    onTabChange,
    onLoadingChange,
    emailFormId = ASSIGN_CERTIFICATE_EMAIL_FORM_ID,
    teamFormId = ASSIGN_CERTIFICATE_TEAM_FORM_ID,
  } = props;
  const { t } = useTranslation("common");
  const wWidth = useWindowWidth();
  const isMd = wWidth >= WIDTH_MD;
  const [internalTab, setInternalTab] = useState<"email" | "team">("email");
  const active = controlledTab ?? internalTab;
  const setActive = (tab: "email" | "team") => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };
  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const onClose = resolveFormClose(onCloseProp, () => {});
  const { user } = useUserStoreContext((store) => ({
    user: store.user,
  }));

  const { data: vendorUsers, loading } = useQuery<{
    vendorUserList: IUser[];
  }>(gqlUser.queries.GET_VENDOR_USER_LIST_LIGHT, {
    variables: {
      vendorUserListInput: {},
    },
    fetchPolicy: "network-only",
  });

  const {
    handleSubmit: handleSendSurveySubmit,
    control,
    formState: { errors },
  } = useForm<ISendCertificateInput>();

  const [sendCertificate, { loading: sendCertificateLoading }] = useMutation<{
    assignCertificateInput: ISendCertificateInput;
  }>(gqlCertificateUser.mutations.SEND_CERTIFICATE_BY_EMAIL, {
    refetchQueries: [gqlCertificate.queries.GET_CERTIFICATES],
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast.success(t("Certificado asignado con éxito"));
      onClose();
    },
    onError(e) {
      toast.error(e.message);
    },
  });

  const [sendCertificateTeams, { loading: sendCertificateTeamsLoading }] =
    useMutation<{
      createFormSendByTeamsInput: ICreateCertificateSendByTeamsInput;
    }>(gqlCertificateUser.mutations.SEND_CERTIFICATE_BY_TEAM, {
      awaitRefetchQueries: true,
      refetchQueries: [gqlCertificate.queries.GET_CERTIFICATES],
      onCompleted: () => {
        toast.success(t("Certificados asignados con éxito"));
        onClose();
      },
      onError(e) {
        toast.error(e.message);
      },
    });

  useEffect(() => {
    onLoadingChange?.(
      sendCertificateLoading || sendCertificateTeamsLoading
    );
  }, [
    onLoadingChange,
    sendCertificateLoading,
    sendCertificateTeamsLoading,
  ]);

  const handleSendEmail = async (data: ISendCertificateInput) => {
    const users = vendorUsers?.vendorUserList
      .filter((item) => (data.users as unknown as string[]).includes(item.id))
      .map((item) => item.id);
    await sendCertificate({
      variables: {
        assignCertificateInput: {
          users: users ? users : [],
          certificate: certificate?.id,
          certificateDate: selectedDate || new Date(),
        },
      },
    });
  };

  const handleSendEmailByTeams = async ({ teamIds }) => {
    sendCertificateTeams({
      variables: {
        assignCertificateInput: {
          certificateId: certificate?.id,
          teamsIds: teamIds,
          certificateDate: selectedDate || new Date(),
        },
      },
    });
  };

  const {
    handleSubmit: handleSendByTeams,
    control: controlByTeams,
    formState: { errors: errorsByTeams },
  } = useForm<ICreateCertificateSendByTeamsInput>();

  return (
    <>
      {!hideHeader ? (
        <p className="mb-3 text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50">
          {t("Asignar certificado")}
        </p>
      ) : null}

      <div className="mb-4">
        <LiquidPillGroup
          activeValue={active}
          onChange={(value) => setActive(value as "email" | "team")}
          items={[
            {
              value: "email",
              label: (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {isMd ? <span>{t("Usuario")}</span> : null}
                </div>
              ),
            },
            {
              value: "team",
              label: (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {isMd ? <span>{t("Equipo")}</span> : null}
                </div>
              ),
            },
          ]}
        />
      </div>

      {active === "email" ? (
        <form
          id={emailFormId}
          onSubmit={handleSendSurveySubmit(handleSendEmail)}
          className="mt-2"
        >
          <div className="grid grid-cols-1 gap-4">
            <div className="col-span-full space-y-1">
              <p className="text-[13px] font-medium leading-snug text-slate-900 dark:text-zinc-50">
                {t("Enviar para")}
              </p>
              <Controller
                name="users"
                control={control}
                defaultValue={[]}
                rules={{ required: true }}
                render={({ field }) => (
                  <TagPickerV2
                    value={field.value as string[]}
                    onChange={(value) => field.onChange(value ?? [])}
                    placeholder={t("Enviar para")}
                    size="filter"
                    data={vendorUsers?.vendorUserList || []}
                    labelKey="email"
                    valueKey="id"
                    loading={loading}
                    renderMenuItem={(_item: any, key: IUser) => {
                      const text = `${key.name || ""} ${key.lastname || ""}`;
                      const email = key.email || "";
                      return (
                        <>
                          <span className="text-[13px] font-medium capitalize leading-snug text-slate-900 dark:text-zinc-50">
                            {text}
                          </span>{" "}
                          <span className="text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
                            · {email}
                          </span>
                        </>
                      );
                    }}
                    searchBy={(
                      keyword: string,
                      _label: React.ReactNode,
                      item: IUser
                    ) =>
                      item.name
                        ?.toLowerCase()
                        .includes(keyword.toLowerCase()) ||
                      item.lastname
                        ?.toLowerCase()
                        .includes(keyword.toLowerCase()) ||
                      item.email?.toLowerCase().includes(keyword.toLowerCase())
                    }
                  />
                )}
              />
              {errors?.users ? (
                <p className="mt-1 text-[9px] leading-snug text-red-500">
                  {t("El correo es requerido")}
                </p>
              ) : null}
            </div>
            <div className="col-span-full space-y-3">
              <SwitcherV2
                dense
                label={t("Fecha de emisión personalizada")}
                description={
                  isSwitchOn
                    ? undefined
                    : t("Fecha de emisión por defecto")
                }
                checked={isSwitchOn}
                onCheckedChange={setIsSwitchOn}
              />
              {isSwitchOn ? (
                <Controller
                  name="certificateDate"
                  control={control}
                  defaultValue={new Date()}
                  rules={{ required: isSwitchOn }}
                  render={({ field }) => (
                    <DatePickerV2
                      {...field}
                      value={field.value || selectedDate}
                      onChange={(date: Date | null) => {
                        field.onChange(date);
                        setSelectedDate(date);
                      }}
                      label={t(
                        "Seleccione la fecha de otorgamiento del certificado"
                      )}
                      placeholder={t(
                        "Seleccione la fecha de otorgamiento del certificado"
                      )}
                      error={
                        errors?.certificateDate
                          ? t("La fecha del certificado es requerida")
                          : undefined
                      }
                      placement="auto"
                    />
                  )}
                />
              ) : null}
            </div>
            {!hideFooter ? (
              <ModalButtons
                compact
                loading={sendCertificateLoading}
                onCancel={onClose}
                submitLabel={t("Enviar")}
              />
            ) : null}
          </div>
        </form>
      ) : null}

      {active === "team" ? (
        <form
          id={teamFormId}
          onSubmit={handleSendByTeams(handleSendEmailByTeams)}
          className="mt-2"
        >
          <div className="grid grid-cols-1 gap-4">
            <div className="col-span-full space-y-1">
              <p className="text-[13px] font-medium leading-snug text-slate-900 dark:text-zinc-50">
                {t("Enviar a los equipos")}
              </p>
              <Controller
                name="teamIds"
                control={controlByTeams}
                defaultValue={[]}
                rules={{ required: true }}
                render={({ field }) => (
                  <TagPickerV2
                    value={field.value as string[]}
                    onChange={(value) => field.onChange(value ?? [])}
                    placeholder={t("Enviar a los equipos")}
                    size="filter"
                    data={user?.teams || []}
                    labelKey="name"
                    valueKey="id"
                    loading={sendCertificateTeamsLoading}
                    creatable={false}
                    searchBy={(
                      keyword: string,
                      _label: React.ReactNode,
                      item: IArea
                    ) =>
                      item.name?.toLowerCase().includes(keyword.toLowerCase())
                    }
                  />
                )}
              />
              {errorsByTeams?.teamIds ? (
                <p className="mt-1 text-[9px] leading-snug text-red-500">
                  {t("Se requiere al menos un equipo")}
                </p>
              ) : null}
            </div>
            <div className="col-span-full space-y-3">
              <SwitcherV2
                dense
                label={t("Establecer fecha de certificado")}
                checked={isSwitchOn}
                onCheckedChange={setIsSwitchOn}
              />
              {isSwitchOn ? (
                <Controller
                  name="certificateDate"
                  control={controlByTeams}
                  defaultValue={new Date()}
                  rules={{ required: isSwitchOn }}
                  render={({ field }) => (
                    <DatePickerV2
                      {...field}
                      value={field.value || selectedDate}
                      onChange={(date: Date | null) => {
                        field.onChange(date);
                        setSelectedDate(date);
                      }}
                      label={t(
                        "Seleccione la fecha de otorgamiento del certificado"
                      )}
                      placeholder={t(
                        "Seleccione la fecha de otorgamiento del certificado"
                      )}
                      error={
                        errorsByTeams?.certificateDate
                          ? t("La fecha del certificado es requerida")
                          : undefined
                      }
                      placement="auto"
                    />
                  )}
                />
              ) : null}
            </div>
            {!hideFooter ? (
              <ModalButtons
                compact
                loading={sendCertificateTeamsLoading}
                onCancel={onClose}
                submitLabel={t("Enviar")}
              />
            ) : null}
          </div>
        </form>
      ) : null}
    </>
  );
};
